import React, { useMemo } from "react";
import { Handle, NodeProps, Position } from "reactflow";
import clsx from "clsx";
import { Info, Link2, Shield, StickyNote, Users } from "lucide-react";

import type { NodeData } from "../lib/api";
import { getContactCounts } from "../lib/contacts";

const contrastColor = (hex?: string | null) => {
  if (!hex) return "#0f172a";
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return "#0f172a";
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#0f172a" : "#f8fafc";
};

const lighten = (hex?: string | null, amount = 0.14) => {
  if (!hex) return "rgba(255,255,255,0.2)";
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return "rgba(255,255,255,0.2)";
  const toChannel = (pair: string) => Math.min(255, Math.round(parseInt(pair, 16) + 255 * amount));
  const r = toChannel(clean.slice(0, 2));
  const g = toChannel(clean.slice(2, 4));
  const b = toChannel(clean.slice(4, 6));
  return `rgb(${r}, ${g}, ${b})`;
};

const countOrLength = (value?: number, fallbackArray?: any[]) =>
  typeof value === "number" ? value : fallbackArray?.length || 0;

const toRgba = (hex?: string | null, alpha = 0.18) => {
  if (!hex || !hex.startsWith("#") || (hex.length !== 7 && hex.length !== 4)) return `rgba(255,255,255,${alpha})`;
  const clean = hex.length === 4 ? hex.slice(1).split("").map((c) => c + c).join("") : hex.slice(1);
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const badge = (icon: React.ReactNode, count: number, label?: string, style?: React.CSSProperties) => (
  <span
    className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold tracking-tight"
    style={style}
  >
    {icon}
    <span className="font-mono">{count}</span>
    {label && <span className="opacity-80">{label}</span>}
  </span>
);

export const AtlasNode: React.FC<NodeProps<NodeData>> = React.memo(({ data, selected }) => {
  const onToggleCollapse = (data as any).onToggleCollapse as (() => void) | undefined;

  const counts = useMemo(() => {
    const contactsCount = countOrLength(data.contactsCount, data.contacts);
    const linksCount = countOrLength(data.linksCount, data.links);
    const notesCount = countOrLength(data.notesCount, data.notes);
    const info = Boolean((data.description && data.description.trim().length > 0) || linksCount > 0 || notesCount > 0);
    return { contactsCount, linksCount, notesCount, info };
  }, [data.contacts, data.contactsCount, data.description, data.links, data.linksCount, data.notes, data.notesCount]);

  const bgColor = data.color || "var(--color-surface)";
  const textColor = data.text_color || contrastColor(bgColor);
  const borderColor = lighten(bgColor, 0.1);
  const collapseCount = data.hiddenDescendantCount || 0;
  const showCollapseBadge = data.collapsed || collapseCount > 0;
  const longLabel = (data.label || "").length > 60;
  const contactCountData = useMemo(() => getContactCounts((data as any).contacts || []), [data.contacts]);
  const boardCount = contactCountData.board;
  const groupCount = contactCountData.group;
  const badgeBgBoard = toRgba(textColor, 0.22);
  const badgeBgGroup = toRgba(textColor, 0.14);
  const badgeBorderBoard = toRgba(textColor, 0.32);

  return (
    <div
      className={clsx(
        "atlas-node relative w-60 rounded-2xl border px-4 py-3 transition-all duration-150",
        "shadow-[0_10px_24px_rgba(0,0,0,0.12)]",
        "hover:shadow-[0_14px_28px_rgba(0,0,0,0.18)]",
        selected ? "atlas-node--selected" : "",
        data.highlighted ? "outline outline-2 outline-white/50 dark:outline-slate-100/50" : "",
        data.inactive ? "opacity-65 border-dashed" : ""
      )}
      style={{ background: bgColor, color: textColor, borderColor, ["--node-text" as any]: textColor }}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div
            className={clsx(
              "text-[13px] font-black leading-snug pr-2 title-font",
              "line-clamp-2 overflow-hidden",
              longLabel ? "text-[12px] leading-tight" : ""
            )}
            title={data.label}
          >
            {data.label}
          </div>
        </div>
      </div>

      {counts.info && (
        <div className="absolute -top-2 -left-2 flex items-center gap-1">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/30 text-[11px] font-bold text-white shadow-sm backdrop-blur">
            <Info size={12} />
          </span>
        </div>
      )}

      {showCollapseBadge && (
        <div className="absolute -top-2 right-2 flex items-center gap-1">
          <button
            type="button"
            disabled={!onToggleCollapse}
            onClick={onToggleCollapse}
            className={clsx(
              "inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-2 py-0.5 text-[11px] font-semibold shadow-sm",
              onToggleCollapse ? "hover:bg-emerald-600 transition" : "cursor-default"
            )}
            title={data.collapsed ? "Ast ausklappen" : "Ast einklappen"}
          >
            {data.collapsed ? "▸" : "▾"} {collapseCount}
          </button>
        </div>
      )}

      <div
        className="mt-2 flex flex-wrap items-center gap-2 text-[11px]"
        title={`Vorstand: ${boardCount} · Gruppe: ${groupCount}`}
        style={{ color: "var(--node-text)" }}
      >
        {boardCount > 0 &&
          badge(<Shield size={14} className="opacity-80" />, boardCount, undefined, {
            background: badgeBgBoard,
            border: `1px solid ${badgeBorderBoard}`,
          })}
        {groupCount > 0 &&
          badge(<Users size={14} className="opacity-80" />, groupCount, undefined, {
            background: badgeBgGroup,
          })}
        {counts.linksCount > 0 &&
          badge(<Link2 size={14} className="opacity-80" />, counts.linksCount, undefined, {
            background: badgeBgGroup,
          })}
        {counts.notesCount > 0 &&
          badge(<StickyNote size={14} className="opacity-80" />, counts.notesCount, undefined, {
            background: badgeBgGroup,
          })}
      </div>

      {/* Handles on four sides (kept for edge rendering; disabled in viewOnly) */}
      <Handle type="source" position={Position.Top} id="top" className="atlas-handle" isConnectable={!data.viewOnly} />
      <Handle type="target" position={Position.Top} id="top" className="atlas-handle" isConnectable={!data.viewOnly} />
      <Handle type="source" position={Position.Bottom} id="bottom" className="atlas-handle" isConnectable={!data.viewOnly} />
      <Handle type="target" position={Position.Bottom} id="bottom" className="atlas-handle" isConnectable={!data.viewOnly} />
      <Handle type="source" position={Position.Left} id="left" className="atlas-handle" isConnectable={!data.viewOnly} />
      <Handle type="target" position={Position.Left} id="left" className="atlas-handle" isConnectable={!data.viewOnly} />
      <Handle type="source" position={Position.Right} id="right" className="atlas-handle" isConnectable={!data.viewOnly} />
      <Handle type="target" position={Position.Right} id="right" className="atlas-handle" isConnectable={!data.viewOnly} />
    </div>
  );
});

AtlasNode.displayName = "AtlasNode";
